// Three thin wrappers around the shared Profile360 component.
// All entity data is fetched via existing APIs; chart data is mocked (per user choice).
import { useQuery } from "@tanstack/react-query";
import { CampusesAPI, InstitutionsAPI, TenantsAPI } from "@/lib/sprint2-api";
import { StudentsAPI, UsersAdminAPI } from "@/lib/sprint3-api";
import { useAuth } from "@/context/AuthContext";
import { Profile360 } from "@/components/profile360/Profile360";

// Deterministic pseudo-random based on id string (so the same entity always shows the same mock numbers).
const seed = (s) => { let h = 0; for (const c of s) h = (h * 31 + c.charCodeAt(0)) | 0; return Math.abs(h); };
const pct = (id, salt, min, max) => min + (seed(id + salt) % (max - min + 1));

const SUBJECTS = ["Math", "Science", "English", "Hindi", "Social"];
const perf = (id) => SUBJECTS.map((s, i) => ({ label: s, value: 55 + (seed(id + s + i) % 45) }));

function attendanceMock(id) {
  const p = pct(id, "att", 60, 95);
  const total = 30;
  return { presentDays: Math.round((p / 100) * total), absentDays: total - Math.round((p / 100) * total), percent: p };
}

function feeMock(id, salt) {
  // Scale the magnitudes so the donut shows real ₹ figures, not 0–100 percentages.
  // - campus salts ("ac"/"tr") roll up to lakhs/crores
  // - staff salts ("trs"/"as") are small (their own transport-share)
  // - student salts ("stuac"/"stutr") sit in the parent-payable annual range
  const isCampus = salt === "ac" || salt === "tr";
  const isStudent = salt.startsWith("stu");
  let base;
  if (isCampus) base = salt === "ac" ? 6_000_000 : 1_500_000;       // 60L / 15L
  else if (isStudent) base = salt === "stuac" ? 120_000 : 18_000;   // 1.2L / 18k
  else base = salt === "trs" ? 15_000 : 60_000;                     // staff
  const variance = (seed(id + salt + "v") % 70) / 100;              // 0..0.7
  const total = Math.round(base * (1 + variance));
  const paidPct = pct(id, salt + "p", 45, 80) / 100;
  const duePct = pct(id, salt + "d", 5, 25) / 100;
  const pendingPct = Math.max(0, 1 - paidPct - duePct);
  const paid = Math.round(total * paidPct);
  const due = Math.round(total * duePct);
  const pending = Math.max(0, total - paid - due);
  return { paid, due, pending, status: paidPct >= 0.6 ? "paid" : "unpaid" };
}

function useTenantParam() {
  const { user } = useAuth();
  const isSuper = user?.roles?.includes("super_admin");
  const tQ = useQuery({ queryKey: ["tenants"], queryFn: () => TenantsAPI.list(), enabled: isSuper });
  const tenantId = isSuper ? tQ.data?.[0]?.id : user?.tenant_id;
  const tenantParam = isSuper && tenantId ? { tenant_id: tenantId } : undefined;
  return { tenantId, tenantParam };
}

export function Campus360() {
  const { tenantParam } = useTenantParam();
  const campusesQ = useQuery({
    queryKey: ["c360-campuses", tenantParam?.tenant_id || "self"],
    queryFn: () => CampusesAPI.list(tenantParam || {}),
    enabled: true,
  });
  const statsQ = useQuery({
    queryKey: ["c360-stats", tenantParam?.tenant_id || "self"],
    queryFn: () => CampusesAPI.stats(tenantParam || {}),
  });
  const instQ = useQuery({
    queryKey: ["c360-inst", tenantParam?.tenant_id || "self"],
    queryFn: () => InstitutionsAPI.list(tenantParam),
  });
  const items = (campusesQ.data || []).map((c) => ({ id: c.id, label: c.name, raw: c }));
  const statsById = new Map((statsQ.data || []).map((s) => [s.campus_id, s]));
  const instName = (iid) => (instQ.data || []).find((i) => i.id === iid)?.name || "";

  return (
    <Profile360
      kind="campus"
      title="Campus 360°"
      items={items}
      getMeta={(it) => {
        const c = it.raw;
        const s = statsById.get(c.id) || { students: 0, classes: 0, sections: 0 };
        return {
          initials: c.name.split(" ").slice(0, 2).map((w) => w[0]).join(""),
          title: c.name,
          subtitle: instName(c.institution_id),
          fields: [
            { label: "Students", value: s.students },
            { label: "Classes", value: s.classes },
            { label: "Sections", value: s.sections },
            { label: "Phone", value: c.phone || "—" },
          ],
        };
      }}
      getCharts={(it) => ({
        attendance: attendanceMock(it.id),
        performance: perf(it.id),
        academicFees: feeMock(it.id, "ac"),
        transportFees: feeMock(it.id, "tr"),
      })}
    />
  );
}

export function Staff360() {
  const { tenantParam } = useTenantParam();
  const staffQ = useQuery({
    queryKey: ["s360-staff-users", tenantParam?.tenant_id || "self"],
    queryFn: () => UsersAdminAPI.list({ ...(tenantParam || {}), limit: 500 }),
  });
  // Treat anyone who is not a student/parent as staff for the 360 view.
  const STUDENT_LIKE = new Set(["student", "parent"]);
  const staff = (staffQ.data || []).filter(
    (u) => !(u.roles || []).every((r) => STUDENT_LIKE.has(r))
  );
  const items = staff.map((s) => ({
    id: s.id,
    label: `${s.name || s.email} · ${(s.roles || ["staff"])[0]}`,
    raw: s,
  }));
  return (
    <Profile360
      kind="staff"
      title="Staff 360°"
      items={items}
      getMeta={(it) => {
        const u = it.raw;
        const name = u.name || u.email;
        return {
          initials: (name.split(" ").map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase(),
          title: name,
          subtitle: (u.roles || []).join(" · ") || "staff",
          fields: [
            { label: "Email", value: u.email },
            { label: "Phone", value: u.phone || "+91 ••• ••• •••" },
            { label: "Role", value: (u.roles || ["staff"])[0] },
          ],
        };
      }}
      getCharts={(it) => ({
        attendance: attendanceMock(it.id),
        performance: perf(it.id),  // for staff: classes-taught performance proxy
        academicFees: { paid: 0, due: 0, pending: 100, status: "paid" }, // staff don't pay fees
        transportFees: feeMock(it.id, "trs"),
      })}
    />
  );
}

export function Student360() {
  const { tenantParam } = useTenantParam();
  const studentsQ = useQuery({
    queryKey: ["st360-students", tenantParam?.tenant_id || "self"],
    queryFn: () => StudentsAPI.list({ limit: 200, ...(tenantParam || {}) }),
  });
  const items = (studentsQ.data || []).map((s) => ({
    id: s.id,
    label: `${s.admission_no} · ${s.first_name} ${s.last_name}`,
    raw: s,
  }));
  return (
    <Profile360
      kind="student"
      title="Student 360°"
      items={items}
      getMeta={(it) => {
        const s = it.raw;
        const name = `${s.first_name || ""} ${s.last_name || ""}`.trim();
        return {
          initials: (name.split(" ").map((w) => w[0]).slice(0, 2).join("") || "?").toUpperCase(),
          title: name,
          subtitle: `Admission ${s.admission_no}`,
          fields: [
            { label: "Class", value: s.class_id ? "Assigned" : "—" },
            { label: "Admission", value: s.admission_no },
            { label: "Gender", value: s.gender || "—" },
            { label: "Phone", value: s.phone || "+91 ••• ••• •••" },
          ],
        };
      }}
      getCharts={(it) => ({
        attendance: attendanceMock(it.id),
        performance: perf(it.id),
        academicFees: feeMock(it.id, "stua"),
        transportFees: feeMock(it.id, "stut"),
      })}
    />
  );
}
